import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";

/**
  Constructs a Quote Slider for Campaign Views.
  --- displays on campaign view only
  --- content is set in customizer and is shared for all campaigns
  --- uses <campaign-quote-slider> and <campaign-quote-nav> custom elements

  todo: update slider.js to fit campaign quote slider

  @property {HTMLElement} slider.container - The container of the slider.
  @property {NodeList} slider.slides - The slides of the slider.
  @property {Object} slider.instance - The KeenSlider instance of the slider.
  @property {HTMLElement} nav.container - The container of the navigation.
  @property {NodeList} nav.links - The navigation dots of the slider.
  @property {HTMLElement} nav.prev - The previous control of the navigation.
  @property {HTMLElement} nav.next - The next control of the navigation.
  @property {string} nav.fill - The fill color of the active dot.
  @property {number} index - Index of the current slider
*/

export class CampaignQuoteSlider {
  constructor() {
    (this.breakpoint = 1024),
      (this.slider = {
        container: document.querySelector("campaign-quote-slider"),
        slides: false,
        instance: false,
      });
    this.nav = {
      container: document.querySelector("campaign-quote-nav"),
      links: false,
      prev: false,
      next: false,
      fill: false,
    };
    this.index = 0;
  }

  /**
    Builds the KeenSlider instance and initializes navigation elements.
  */
  _build = () => {
    if (this.slider.container.hasAttribute("data-keen-slider-disabled")) {
      this.slider.container.removeAttribute("data-keen-slider-disabled");
    }

    this.nav.links = this.nav.container.querySelectorAll("nav-dot");
    this.nav.prev = this.nav.container.querySelector("control-prev");
    this.nav.next = this.nav.container.querySelector("control-next");
    this.nav.fill = this.nav.container.dataset.fill;

    this.slider.instance = new KeenSlider(this.slider.container, {
      selector: this.slider.slides,
      slides: {
        perView: 1,
        spacing: 20,
      },
      loop: true,
      created: (data) => {
        this.slider.slides[data.track.details.rel].setAttribute(
          "aria-hidden",
          false
        );
        this._buildNav();
      },
      slideChanged: (data) => {
        this._updateSlider(data.track.details.rel);
      },
      destroyed: (data) => {
        this.slider.container.setAttribute("data-keen-slider-disabled", null);
        this._resetSlides();
      },
    });
  };

  /**
    Updates the slider instance with new slide index.
    @param {number} newIdx - The new index of the slide to update the slider with.
  */
  _updateSlider = (newIdx) => {
    this.slider.slides[this.index].setAttribute("aria-hidden", true);
    this.slider.slides[newIdx].setAttribute("aria-hidden", false);

    this.nav.links[this.index].style.background = "transparent";
    this.nav.links[this.index].setAttribute("aria-current", false);
    this.nav.links[newIdx].style.background = `${this.nav.fill}`;
    this.nav.links[newIdx].setAttribute("aria-current", true);

    this.index = newIdx;
  };

  /**
    Handles the click event on the navigation dots and moves the slider instance to the selected slide index.
    @param {Object} e - The event object.
  */
  _handleClick = (e) => {
    const index = e.currentTarget.dataset.key;
    this.slider.instance.moveToIdx(parseInt(index));
  };

  // shift slider to previous slider
  _handlePrev = () => {
    this.slider.instance.prev();
  };

  // shift slider to next slide
  _handleNext = () => {
    this.slider.instance.next();
  };

  _buildNav = () => {
    this.nav.links[this.index].style.background = `${this.nav.fill}`;
    this.nav.links.forEach((el) => {
      el.addEventListener("click", this._handleClick);
    });

    this.nav.prev.addEventListener("click", this._handlePrev);
    this.nav.next.addEventListener("click", this._handleNext);
  };

  _resetSlides = () => {
    this.slider.slides.forEach((el) => {
      el.style.transform = "0";
      el.removeAttribute("aria-hidden");
      el.setAttribute("aria-hidden", false);
    });
  };

  _setReload = () => {
    window.addEventListener("recharge:reload", () => {
      if (this.slider.instance) {
        this.slider.instance.destroy();
      }

      this.slider.container = document.querySelector("campaign-quote-slider");
      this.nav.container = document.querySelector("campaign-quote-nav");
      this.slider.slides = this.slider.container.childNodes;

      if (window.innerWidth < 1024 && this.slider.slides) {
        this._build();
      }
    });
  };

  _init = () => {
    this.slider.slides =
      this.slider.container != null ? this.slider.container.childNodes : false;

    if (this.slider.slides == false || this.slider.slides.length <= 1) {
      return;
    }

    if (window.innerWidth < 1024) {
      this._build();
    }

    this._setReload();

    window.addEventListener("resize", () => {
      if (window.innerWidth < 1024 && this.slider.instance == false) {
        this._build();
      }
      if (window.innerWidth >= 1024 && this.slider.instance) {
        this.slider.instance.destroy();
      }
    });
  };
}
